import { useEffect, useState, useCallback } from "react";
import { Main } from "@/components/Main";
import { CreateLoaderTable } from "@/components/Loaders";
import {
  getStocksSellersSummary,
  getStocksSellersSummaryDetails,
  getStocksSellersPendingDetails,
  StockSellersSummaryFilters,
  exportStocksSellersSummary,
} from "@/Api/stocks";
import { getSelectSellers } from "@/Api/sellers";
import { getSelectProductGroups } from "@/Api/productGroups";
import { getSelectContests } from "@/Api/contests";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Eye, Filter, X, MoreHorizontal, RefreshCw, Loader2, Download, FileText, FileSpreadsheet, AlertCircle, ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { Mask } from "@/utils/mask";
import { formatDate } from "@/utils/format-date";
import { MultiSelectCheckbox } from "@/components/ui/multi-select-checkbox";

interface StockSummaryItem {
  contest: {
    id: number;
    uuid: string;
    name: string;
    validation_date: string;
  };
  lottery: {
    id: number;
    name: string;
    color: string;
  };
  summary: {
    total_sellers: number;
    total_products: number;
    total_balance: number;
    total_pending?: number;
  };
}

interface PendingProduct {
  cp_classification_id: number;
  product_name: string;
  product_classification_name: string;
  sale_price: number;
  quota_quantity: number;
  total_value: number;
}

interface PendingSellerDetail {
  seller_id: number;
  seller_name: string;
  products: PendingProduct[];
  total_quantity: number;
  total_value: number;
}

interface SellerDetail {
  seller_id: number;
  seller_name: string;
  balance: number;
  total_value?: number;
}

interface ProductDetail {
  cp_classification_id: number;
  product_name: string;
  product_classification: {
    id: number;
    name: string;
    sale_price: number;
  };
  total_balance: number;
  total_sellers: number;
  sellers: SellerDetail[];
}

export const PageList = () => {
  const [data, setData] = useState<StockSummaryItem[]>([]);
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1 });
  const [state, setState] = useState<"loading" | "production">("loading");
  const [sellers, setSellers] = useState<any[]>([]);
  const [lotteries, setLotteries] = useState<any[]>([]);
  const [contests, setContests] = useState<any[]>([]);
  const [filters, setFilters] = useState<StockSellersSummaryFilters>({});
  const [showFilters, setShowFilters] = useState(false);
  const [selectedSellers, setSelectedSellers] = useState<number[]>([]);
  const [selectedLotteries, setSelectedLotteries] = useState<number[]>([]);
  const [selectedContest, setSelectedContest] = useState<string>("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalData, setModalData] = useState<{
    contest: StockSummaryItem["contest"];
    lottery: StockSummaryItem["lottery"];
    products: ProductDetail[];
  } | null>(null);
  const [isLoadingModal, setIsLoadingModal] = useState(false);

  const [pendingData, setPendingData] = useState<{
    [contestId: number]: {
      loading: boolean;
      data: PendingSellerDetail[];
    };
  }>({});

  const [isPendingModalOpen, setIsPendingModalOpen] = useState(false);
  const [selectedPendingItem, setSelectedPendingItem] = useState<StockSummaryItem | null>(null);

  const [expandedSellers, setExpandedSellers] = useState<Set<number>>(new Set());
  const [expandedPendingSellers, setExpandedPendingSellers] = useState<Set<string>>(new Set());
  const [sellerProductSearchTerms, setSellerProductSearchTerms] = useState<Record<number, string>>({});

  const toggleSeller = (sellerId: number) => {
    setExpandedSellers((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(sellerId)) {
        newSet.delete(sellerId);
      } else {
        newSet.add(sellerId);
      }
      return newSet;
    });
  };

  const togglePendingSeller = (contestId: number, sellerId: number) => {
    const key = `${contestId}-${sellerId}`;
    setExpandedPendingSellers((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  const fetchData = useCallback(
    async (page: number = 1) => {
      setState("loading");
      try {
        const { data: response } = await getStocksSellersSummary(page, filters);
        const responseData = response?.response;

        if (Array.isArray(responseData)) {
          const filteredData = responseData.filter(
            (item: StockSummaryItem) => item.summary.total_products > 0
          );
          setData(filteredData);
          setPagination({
            current_page: response?.response?.current_page || 1,
            last_page: response?.response?.last_page || 1,
          });
        } else if (responseData?.data) {
          const filteredData = responseData.data.filter(
            (item: StockSummaryItem) => item.summary.total_products > 0
          );
          setData(filteredData);
          setPagination({
            current_page: responseData.current_page || 1,
            last_page: responseData.last_page || 1,
          });
        } else {
          setData([]);
        }
      } catch (error) {
        console.error("Erro ao buscar dados:", error);
        setData([]);
      } finally {
        setState("production");
      }
    },
    [filters]
  );

  const fetchOptions = async () => {
    try {
      const [sellersRes, lotteriesRes, contestsRes] = await Promise.all([
        getSelectSellers({ is_active: true }),
        getSelectProductGroups(),
        getSelectContests(),
      ]);
      setSellers(sellersRes.data?.response || []);
      setLotteries(lotteriesRes.data?.response || []);
      setContests(contestsRes.data?.response || []);
    } catch (error) {
      console.error("Erro ao buscar opções:", error);
    }
  };

  useEffect(() => {
    fetchData(1);
    fetchOptions();
  }, []);

  useEffect(() => {
    fetchData(1);
  }, [filters]);

  const handleApplyFilters = () => {
    setFilters({
      seller_id: selectedSellers.length > 0 ? selectedSellers : undefined,
      lottery_id: selectedLotteries.length > 0 ? selectedLotteries : undefined,
      contest_id: selectedContest ? Number(selectedContest) : undefined,
      start_date: startDate || undefined,
      end_date: endDate || undefined,
    });
  };

  const handleClearFilters = () => {
    setSelectedSellers([]);
    setSelectedLotteries([]);
    setSelectedContest("");
    setStartDate("");
    setEndDate("");
    setFilters({});
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchData(pagination.current_page || 1);
    setIsRefreshing(false);
  };

  const handleExport = async (format: "pdf" | "xlsx") => {
    setIsExporting(true);
    try {
      const blob = await exportStocksSellersSummary({
        ...filters,
        format,
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `estoque-vendedores-${new Date().toISOString().split("T")[0]}.${format === "pdf" ? "pdf" : "xlsx"}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success(`Exportado com sucesso em ${format.toUpperCase()}`);
    } catch (error) {
      console.error("Error exporting:", error);
      toast.error("Erro ao exportar estoque");
    } finally {
      setIsExporting(false);
    }
  };

  const handleViewProducts = async (item: StockSummaryItem) => {
    setIsModalOpen(true);
    setIsLoadingModal(true);
    setModalData({
      contest: item.contest,
      lottery: item.lottery,
      products: [],
    });

    try {
      const { data: response } = await getStocksSellersSummaryDetails(
        item.contest.id,
        filters
      );
      const products = response?.response || [];
      setModalData({
        contest: item.contest,
        lottery: item.lottery,
        products: Array.isArray(products) ? products : [],
      });
    } catch (error) {
      console.error("Erro ao buscar detalhes:", error);
    } finally {
      setIsLoadingModal(false);
    }
  };

  const handleViewPending = async (item: StockSummaryItem) => {
    setSelectedPendingItem(item);
    setIsPendingModalOpen(true);

    if (pendingData[item.contest.id]?.data) return;

    setPendingData((prev) => ({
      ...prev,
      [item.contest.id]: { loading: true, data: [] },
    }));

    try {
      const { data: response } = await getStocksSellersPendingDetails(item.contest.id);
      const sellers = response?.response || [];
      setPendingData((prev) => ({
        ...prev,
        [item.contest.id]: { loading: false, data: Array.isArray(sellers) ? sellers : [] },
      }));
    } catch (error) {
      console.error("Erro ao buscar pendentes:", error);
      setPendingData((prev) => ({
        ...prev,
        [item.contest.id]: { loading: false, data: [] },
      }));
    }
  };

  const pageActions = {
    next: async () => {
      if (pagination.current_page < pagination.last_page) {
        await fetchData(pagination.current_page + 1);
      }
    },
    previous: async () => {
      if (pagination.current_page > 1) {
        await fetchData(pagination.current_page - 1);
      }
    },
    lastPage: async () => {
      if (pagination.current_page !== pagination.last_page) {
        await fetchData(pagination.last_page);
      }
    },
  };

  return (
    <Main>
      <div className="flex justify-center md:justify-between items-center flex-wrap gap-2">
        <h2 className="text-2xl font-semibold flex gap-2 items-center">
          Estoque por Vendedor
        </h2>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={isRefreshing}
          >
            <RefreshCw
              className={cn("h-4 w-4 mr-2", isRefreshing && "animate-spin")}
            />
            Atualizar
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                disabled={isExporting}
              >
                {isExporting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Exportando...
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4 mr-2" />
                    Exportar
                  </>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleExport("pdf")}>
                <FileText className="h-4 w-4 mr-2" />
                Exportar PDF
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport("xlsx")}>
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                Exportar Excel
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter className="h-4 w-4 mr-2" />
            Filtros
          </Button>
        </div>
      </div>

      {showFilters && (
        <div className="bg-zinc-50 dark:bg-zinc-900 p-4 rounded-lg space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Vendedor</Label>
              <Select
                onValueChange={(value) => {
                  if (value && value !== "none") {
                    const sellerId = Number(value);
                    if (!selectedSellers.includes(sellerId)) {
                      setSelectedSellers([...selectedSellers, sellerId]);
                    }
                  }
                }}
                value=""
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione vendedores" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Todos os vendedores</SelectItem>
                  {sellers.map((seller) => (
                    <SelectItem
                      key={seller.id}
                      value={seller.id.toString()}
                      disabled={selectedSellers.includes(seller.id)}
                    >
                      {seller?.user?.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedSellers.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {selectedSellers.map((sellerId) => {
                    const seller = sellers.find((s) => s.id === sellerId);
                    return (
                      <Badge
                        key={sellerId}
                        variant="secondary"
                        className="flex items-center gap-1"
                      >
                        {seller?.user?.name}
                        <X
                          className="h-3 w-3 cursor-pointer hover:text-red-500"
                          onClick={() =>
                            setSelectedSellers(
                              selectedSellers.filter((id) => id !== sellerId)
                            )
                          }
                        />
                      </Badge>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label>Loteria</Label>
              <MultiSelectCheckbox
                options={lotteries.map((lottery) => ({
                  value: lottery.id.toString(),
                  label: lottery.name,
                }))}
                selectedValues={selectedLotteries.map(String)}
                onSelectionChange={(values) => {
                  setSelectedLotteries(values.map(Number));
                }}
                placeholder="Selecione loterias"
                selectAllLabel="SELECIONAR TUDO"
              />
            </div>

            <div className="space-y-2">
              <Label>Concurso</Label>
              <Select
                value={selectedContest}
                onValueChange={setSelectedContest}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione um concurso" />
                </SelectTrigger>
                <SelectContent>
                  {contests.map((contest) => (
                    <SelectItem key={contest.id} value={contest.id.toString()}>
                      {contest.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Data Inicial</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>Data Final</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>

          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={handleClearFilters}>
              Limpar
            </Button>
            <Button onClick={handleApplyFilters}>Aplicar Filtros</Button>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-3">
        {state === "loading" ? (
          <CreateLoaderTable cols={6} lines={6} />
        ) : (
          <>
            <Table className="bg-zinc-50 rounded dark:bg-[#2c2c2c]">
              <TableHeader>
                <TableRow>
                  <TableHead>Loteria</TableHead>
                  <TableHead>Concurso</TableHead>
                  <TableHead>Data de Validade</TableHead>
                  <TableHead className="text-center">Não Aceitos</TableHead>
                  <TableHead className="text-center">Produtos</TableHead>
                  {/* <TableHead className="text-right">Valor</TableHead> */}
                </TableRow>
              </TableHeader>
              <TableBody>
                {data && data.length > 0 ? (
                  data.map((item) => (
                    <TableRow key={`${item.contest.id}-${item.lottery.id}`}>
                      <TableCell>
                        <Badge
                          className="text-xs text-white border-0"
                          style={{
                            backgroundColor: item.lottery.color || "#000",
                          }}
                        >
                          {item.lottery.name}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-medium">
                        {item.contest.name}
                      </TableCell>
                      <TableCell>
                        {formatDate(item.contest.validation_date)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-center gap-2">
                          {(item.summary.total_pending ?? 0) > 0 ? (
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => handleViewPending(item)}
                              className="flex items-center gap-2"
                            >
                              <Eye className="h-4 w-4" />
                              {item.summary.total_pending}
                            </Button>
                          ) : (
                            <span className="text-muted-foreground text-sm">-</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewProducts(item)}
                            className="flex items-center gap-2 text-gray-200 dark:text-gray-200"
                          >
                            <Eye className="h-4 w-4" />
                            {item.summary.total_products}
                          </Button>
                        </div>
                      </TableCell>
                      {/* <TableCell className="text-right font-semibold">
                        {Mask.formatCurrency(item.summary.total_balance)}
                      </TableCell> */}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8">
                      Nenhum estoque encontrado
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>

            <div className="flex justify-between mb-3">
              {data && data.length > 0 && (
                <span className="font-medium">
                  Exibindo {pagination.current_page} de um total de {pagination.last_page}
                </span>
              )}
              {pagination.last_page > 1 && (
                <Pagination>
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious
                        href="#"
                        className={cn("h-8", colors.bg_primary)}
                        onClick={pageActions.previous}
                      />
                    </PaginationItem>
                    <PaginationItem>
                      <PaginationLink
                        href="#"
                        className={cn(
                          `size-8 bg-transparent border-2 border-[#023859] text-slate-900 font-bold cursor-auto hover:bg-transparent`
                        )}
                      >
                        <span className="pointer-events-none dark:text-[#F4F4F5]">
                          {pagination.current_page}
                        </span>
                      </PaginationLink>
                    </PaginationItem>
                    <PaginationItem>
                      <PaginationLink
                        href="#"
                        className="text-black h-8 hover:bg-stone-950/10"
                        onClick={pageActions.lastPage}
                      >
                        <MoreHorizontal className="h-4 w-4 dark:text-[#F4F4F5]" />
                      </PaginationLink>
                    </PaginationItem>
                    <PaginationItem>
                      <PaginationNext
                        href="#"
                        className={cn("h-8", colors.bg_primary)}
                        onClick={pageActions.next}
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              )}
            </div>
          </>
        )}
      </div>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Produtos:</DialogTitle>
          </DialogHeader>

          {modalData && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-4">
                <Badge
                  className="text-xs text-white border-0"
                  style={{
                    backgroundColor: modalData.lottery.color || "#000",
                  }}
                >
                  {modalData.lottery.name}
                </Badge>
                <span className="font-medium">{modalData.contest.name}</span>
              </div>

              {isLoadingModal ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
                </div>
              ) : modalData.products && modalData.products.length > 0 ? (
                <div className="space-y-2">
                  {(() => {
                    const sellerMap = new Map<number, {
                      seller_name: string;
                      total_balance: number;
                      products: Array<{
                        product_name: string;
                        product_classification_name: string;
                        sale_price: number;
                        balance: number;
                        total_value: number;
                      }>;
                    }>();

                    modalData.products.forEach((product) => {
                      product.sellers?.forEach((seller) => {
                        if (!sellerMap.has(seller.seller_id)) {
                          sellerMap.set(seller.seller_id, {
                            seller_name: seller.seller_name,
                            total_balance: 0,
                            products: [],
                          });
                        }
                        const sellerEntry = sellerMap.get(seller.seller_id)!;
                        sellerEntry.total_balance += seller.balance;
                        sellerEntry.products.push({
                          product_name: product.product_name,
                          product_classification_name: product.product_classification?.name || "",
                          sale_price: product.product_classification?.sale_price || 0,
                          balance: seller.balance,
                          total_value: seller.total_value || 0,
                        });
                      });
                    });

                    return Array.from(sellerMap.entries()).map(([sellerId, sellerData]) => {
                      const isExpanded = expandedSellers.has(sellerId);
                      const searchTerm = sellerProductSearchTerms[sellerId] || "";
                      const filteredProducts = searchTerm
                        ? sellerData.products.filter((product) =>
                            product.product_name.replace(/^[A-Za-z]+/, "").startsWith(searchTerm)
                          )
                        : sellerData.products;

                      return (
                        <Collapsible
                          key={sellerId}
                          className="border rounded-lg"
                          open={isExpanded}
                          onOpenChange={() => toggleSeller(sellerId)}
                        >
                          <CollapsibleTrigger className="flex items-center justify-between w-full p-4 hover:bg-muted/50 transition-colors rounded-lg">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-base">{sellerData.seller_name}</span>
                              <Badge variant="secondary" className="text-xs">
                                {sellerData.total_balance} cotas
                              </Badge>
                            </div>
                            <ChevronDown className={cn("h-4 w-4 transition-transform duration-200", isExpanded && "rotate-180")} />
                          </CollapsibleTrigger>
                          <CollapsibleContent className="px-4 pb-4 space-y-2">
                            <div
                              className="flex items-center gap-2 pb-2"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Label htmlFor={`seller-product-search-${sellerId}`} className="text-sm font-medium">
                                B:
                              </Label>
                              <Input
                                id={`seller-product-search-${sellerId}`}
                                type="number"
                                inputMode="numeric"
                                placeholder="Buscar por número"
                                value={searchTerm}
                                onChange={(e) => {
                                  const value = e.target.value.replace(/\D/g, "");
                                  setSellerProductSearchTerms((prev) => ({
                                    ...prev,
                                    [sellerId]: value,
                                  }));
                                }}
                                className="max-w-[200px]"
                              />
                            </div>
                            {filteredProducts.length === 0 ? (
                              <p className="text-sm text-muted-foreground text-center py-4">
                                Nenhum produto encontrado com esse número
                              </p>
                            ) : (
                              filteredProducts.map((product, index) => (
                                <div
                                  key={index}
                                  className="text-sm text-muted-foreground border-t pt-2 first:border-t-0 first:pt-0"
                                >
                                  <span className="font-medium text-foreground">
                                    {product.product_classification_name}
                                  </span>
                                  -{product.product_name} - {Mask.formatCurrency(product.sale_price)}
                                  <br />
                                  <span className="text-xs">
                                    Quantidade: {product.balance} cotas
                                  </span>
                                </div>
                              ))
                            )}
                          </CollapsibleContent>
                        </Collapsible>
                      );
                    });
                  })()}
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-8">
                  Nenhum produto encontrado
                </p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={isPendingModalOpen} onOpenChange={setIsPendingModalOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-red-500" />
              Não Aceitos
            </DialogTitle>
          </DialogHeader>

          {selectedPendingItem && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-4">
                <Badge
                  className="text-xs text-white border-0"
                  style={{
                    backgroundColor: selectedPendingItem.lottery.color || "#000",
                  }}
                >
                  {selectedPendingItem.lottery.name}
                </Badge>
                <span className="font-medium">{selectedPendingItem.contest.name}</span>
              </div>

              {pendingData[selectedPendingItem.contest.id]?.loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
                </div>
              ) : pendingData[selectedPendingItem.contest.id]?.data?.length > 0 ? (
                <div className="space-y-2">
                  {pendingData[selectedPendingItem.contest.id].data.map((seller) => {
                    const isExpanded = expandedPendingSellers.has(`${selectedPendingItem.contest.id}-${seller.seller_id}`);
                    return (
                      <Collapsible
                        key={seller.seller_id}
                        className="border rounded-lg"
                        open={isExpanded}
                        onOpenChange={() => togglePendingSeller(selectedPendingItem.contest.id, seller.seller_id)}
                      >
                        <CollapsibleTrigger className="flex items-center justify-between w-full p-4 hover:bg-muted/50 transition-colors rounded-lg">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-base">{seller.seller_name}</span>
                            <Badge variant="secondary" className="text-xs">
                              {seller.total_quantity} cotas
                            </Badge>
                          </div>
                          <ChevronDown className={cn("h-4 w-4 transition-transform duration-200", isExpanded && "rotate-180")} />
                        </CollapsibleTrigger>
                        <CollapsibleContent className="px-4 pb-4 space-y-2">
                          {seller.products.map((product, idx) => (
                            <div key={idx} className="text-sm text-muted-foreground border-t pt-2 first:border-t-0 first:pt-0">
                              <span className="font-medium text-foreground">
                                {product.product_classification_name}
                              </span>
                              -{product.product_name} - {Mask.formatCurrency(product.sale_price)}
                              <br />
                              <span className="text-xs">
                                Quantidade: {product.quota_quantity} cotas
                              </span>
                            </div>
                          ))}
                        </CollapsibleContent>
                      </Collapsible>
                    );
                  })}
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-8">
                  Nenhum pendente encontrado
                </p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Main>
  );
};
